<template>
    <b-row>
        <b-col md="12">
            <ValidationObserver ref="importform">
                <select-with-validation
                    vid="card_series_id"
                    label="Lägg till mottagare i serie"
                    rules="required"
                    :options="cardSeriesIds"
                    v-model="cardSeriesId"
                    :disabled="importResults.done"
                ></select-with-validation>
                <b-form-group label="Mottagare">
                    <b-form-textarea
                        debounce="500"
                        rows="10"
                        @input="reparseCsv"
                        v-model="exportCsv.raw"
                        :disabled="importResults.done"
                    ></b-form-textarea>
                </b-form-group>
            </ValidationObserver>
        </b-col>
        <b-col md="12">
            <b-card no-body v-if="exportCsv.parsed.length">
                <b-card-header>
                    <b-row>
                        <b-col md="4">
                            <b-pagination
                                first-number
                                last-number
                                class="mx-auto"
                                v-model="exportCsv.currentPage"
                                :total-rows="exportCsv.parsed.length"
                                :per-page="exportCsv.perPage"
                                aria-controls="export-mailing-list"
                            ></b-pagination>
                        </b-col>
                        <b-col md="4">
                        </b-col>
                        <b-col md="4" class="flex-center-right">
                            <div v-if="exportCsv.parsed.length">
                                Visar {{ Math.min((exportCsv.perPage * (exportCsv.currentPage - 1)) + 1, exportCsv.parsed.length) }}-{{ Math.min(exportCsv.perPage * exportCsv.currentPage, exportCsv.parsed.length) }} av {{ exportCsv.parsed.length }}st
                            </div>
                        </b-col>
                    </b-row>
                </b-card-header>
                <b-table
                    id="export-mailing-list"
                    responsive
                    :items="exportCsv.parsed"
                    :perPage="exportCsv.perPage"
                    :current-page="exportCsv.currentPage"
                >
                </b-table>
                <b-card-footer>
                    <div
                        v-if="!importResults.done"
                    >
                        <div class="alert alert-danger alert-styled-left" v-if="exportCsv.parsed.filter((row) => row.error).length > 0">
                            Listan innehåller fel...
                            <div
                                v-for="(row, ri) in exportCsv.parsed.filter((row) => row.error)"
                                :key="'row-err-' + ri"
                            >
                                Rad {{ row.row }}: {{ row.error }} ({{ row.email }})
                            </div>
                        </div>
                        <b-button
                            v-if="!importResults.done"
                            :variant="errorLevel"
                            @click="importData"
                            :disabled="invalidForm || invalidCsv"
                            class="btn-block"
                        >
                            <b-spinner v-if="loading" small></b-spinner>
                            Importera lista {{ errorLevel === 'warning' ? ('med ' + this.messages.length + ' varningar') : ''}}
                        </b-button>
                    </div>
                    <b-button
                        v-if="importResults.done"
                        @click="resetImport"
                        variant="primary"
                        class="btn-block"
                    >
                        Importerat {{ importResults.data.length }}st med {{ importResults.errored.length }}st fel.
                    </b-button>
                </b-card-footer>
            </b-card>
        </b-col>
        <b-col md="12">
            <div v-if="messages.length">
                <b-button
                    v-b-toggle.csv-collapse-1
                    :variant="errorLevel"
                    style="margin-bottom: 1rem"
                >
                    Inspektera {{ messages.length }}st problem
                </b-button>
                <b-collapse id="csv-collapse-1">
                    <b-card>
                        <b-alert show v-for="item in messages" variant="warning" v-bind:key="item">{{ item }}</b-alert>
                        <b-alert show v-if="messages.length">
                            <p>Allowed headers are:</p>
                            <ul>
                                <li v-for="(item, i_index) in allowedColumns" :key="i_index">{{ item }}</li>
                            </ul>
                            <div>
                                Example _> <br/>
                                <hr/>
                                email,first_name,last_name <br/>
                                sven.inge@supekort.se,Sven-Inge,Yolosson <br/>
                                test@test.com,Test,Testsson <br/>
                                yolo.yolossson@example.se,Yolo,Yolosson <br/>
                            </div>
                        </b-alert>
                    </b-card>
                </b-collapse>
            </div>
        </b-col>
    </b-row>
</template>
<script>
import apiClient from '@services/ApiClient';
import { notifySend } from '@services/Notify';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';
import getError from '@services/ErrorHelper';
import { debounce } from 'lodash';

export default {
    name: 'CsvExporter',
    components: {
        SelectWithValidation,
    },
    props: {
        cardOrderId: {
            required: true,
        },
        cardSeriesIds: {
            type: Array,
            required: true,
        },
    },
    watch: {
        cardSeriesId() {
            this.checkDuplicates(this.invalidCsv);
        },
    },
    data() {
        return {
            cardSeriesId: null,
            loading: false,
            messages: [],
            importResults: {
                done: false,
                errored: [],
                data: [],
            },
            invalidForm: false,
            invalidCsv: false,
            exportCsv: {
                raw: 'email',
                parsed: [],
                perPage: 15,
                currentPage: 1,
            },
            allowedColumns: [
                'email',
                'first_name',
                'last_name',
                'variable_01',
                'variable_02',
                'variable_03',
                'variable_04',
                'variable_05',
                'variable_06',
                'variable_07',
                'variable_08',
                'variable_09',
                'variable_10',
                'test_card_mode',
            ],
        };
    },
    computed: {
        errorLevel() {
            if (this.invalidForm || this.invalidCsv) {
                return 'danger';
            }

            if (this.messages.length) {
                return 'warning';
            }

            return 'success';
        },
    },
    methods: {
        importData() {
            const data = {
                card_order_id: this.cardOrderId,
                card_series_id: this.cardSeriesId,
                recipients: this.exportCsv.parsed,
            };

            this.loading = true;

            apiClient.post(
                '/vue/ecard/recipients/import',
                data,
            ).then((res) => {
                notifySend('Sparning lyckades', 'Sparning lyckades', 'success');
                this.$emit('csv:exported');

                this.importResults.done = true;
                this.importResults.errored = res.data.errored;
                this.importResults.data = res.data.data;
            }).catch((error) => {
                const parsederror = getError(error);
                if (typeof parsederror === 'object') {
                    this.$refs.importform.setErrors(parsederror);
                    Object.keys(parsederror).forEach((key) => {
                        const splitKey = key.split('.');
                        if (splitKey[0] === 'recipients') {
                            if (this.exportCsv.parsed[splitKey[1]]) {
                                this.exportCsv.parsed[splitKey[1]]._rowVariant = 'danger';
                                this.exportCsv.parsed[splitKey[1]].error = parsederror[key][0];
                            }
                        }
                    });
                }
                this.invalidForm = true;
            }).finally(() => {
                this.loading = false;
            });
        },
        resetImport() {
            this.importResults = {
                done: false,
                errored: [],
                data: [],
            };
            this.invalidForm = false;
            this.invalidCsv = false;
            this.messages = [];
            this.exportCsv.parsed = [];
            this.exportCsv.raw = 'email';
        },
        validateHeader(header) {
            header.forEach((item) => {
                if (!this.allowedColumns.includes(item)) {
                    this.messages.push('Unknown column: ' + item);
                }
            });
        },
        emailIsValid(email) {
            return /\S+@\S+\.\S+/.test(email);
        },
        createNewItemObject(index, header) {
            const itemObject = {
                row: index + 1,
            };

            header.forEach((item) => {
                if (this.allowedColumns.includes(item)) {
                    itemObject[item] = null;
                }
            });

            // Quick fix
            // itemObject.test_card_mode = false;
            // itemObject.error = null;

            return itemObject;
        },
        reparseCsv: debounce(function reparseCsv(data) {
            this.loading = true;
            let invalidCsv = false;
            this.messages = [];
            const rawData = data.split(/\r?\n/);
            const firstLine = rawData.shift().split(',').map((item) => item.replace(/['"]+/g, '').replace(' ', ''));

            this.validateHeader(firstLine);

            if (!firstLine.some((item) => item.includes('email'))) {
                this.messages.push('"email" column is required');
                invalidCsv = true;
            }

            this.duplicateCounter = {};
            this.exportCsv.parsed = rawData
                .filter((item) => item.length)
                .map((item, itemIdx) => {
                    const splitItem = item.split(',');
                    const itemObject = this.createNewItemObject(itemIdx, firstLine);
                    firstLine.forEach((itm, index) => {
                        itemObject[itm] = splitItem[index].replace(/['"]+/g, '').replace(' ', '');

                        if (itm === 'email') {
                            if (!this.emailIsValid(itemObject.email)) {
                                itemObject.error = 'Ej korrekt e-postadress.';
                                // this.messages.push(`discovered a invalid email index line: ${parseIndex} address: ${itemObject.email}`);
                                // eslint-disable-next-line no-underscore-dangle
                                itemObject._rowVariant = 'danger';
                                invalidCsv = true;
                            }

                            if (typeof this.duplicateCounter[itemObject.email] === 'undefined') {
                                this.duplicateCounter[itemObject.email] = 1;
                            } else {
                                // eslint-disable-next-line no-underscore-dangle
                                itemObject._rowVariant = 'warning';
                                itemObject.error = 'Dubblett av e-post i listan';
                                // eslint-disable-next-line no-plusplus
                                this.duplicateCounter[itemObject.email]++;
                            }
                        }
                    });
                    return itemObject;
                });

            Object.entries(this.duplicateCounter).forEach(([key, value]) => {
                if (value > 1) {
                    this.messages.push(`duplicate found ${key} counted ${value} entries in textarea`);
                }
            });

            this.$emit('input', this.exportCsv.parsed);

            this.checkDuplicates(invalidCsv);
        }, 500),
        checkDuplicates: debounce(async function checkDuplicates(invalidCsv = false) {
            let invalidForm = false;
            if (!invalidCsv) {
                await apiClient.post(
                    '/vue/ecard/recipients/import/check-duplicates',
                    {
                        card_order_id: this.cardOrderId,
                        card_series_id: this.cardSeriesId,
                        recipients: this.exportCsv.parsed,
                    },
                ).then((res) => {
                    Object.entries(res.data.duplicates).forEach(([key, value]) => {
                        const entry = this.exportCsv.parsed.find((item) => item.email === key);
                        if (entry) {
                            // eslint-disable-next-line no-underscore-dangle
                            entry._rowVariant = 'warning';
                        }
                        this.messages.push('duplicate email found in database table: ' + key + ' counted: ' + value);
                    });
                }).catch((error) => {
                    const parsederror = getError(error);
                    if (typeof parsederror === 'object') {
                        this.$refs.importform.setErrors(parsederror);
                        Object.keys(parsederror).forEach((key) => {
                            const splitKey = key.split('.');
                            if (splitKey[0] === 'recipients') {
                                if (this.exportCsv.parsed[splitKey[1]]) {
                                    this.exportCsv.parsed[splitKey[1]]._rowVariant = 'danger';
                                    this.exportCsv.parsed[splitKey[1]].error = parsederror[key][0].replace(/recipients\.\d+\.email\s*/g, '');
                                }
                            }
                        });
                        // eslint-disable-next-line no-param-reassign
                        invalidForm = true;
                    }
                });
            }

            this.invalidForm = invalidForm;
            this.invalidCsv = invalidCsv;
            this.loading = false;
        }, 1000),
    },
};
</script>
<style>
.flex-center-right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
}

@-webkit-keyframes spinner-border {
    to {
        -webkit-transform: rotate(1turn);
        transform: rotate(1turn)
    }
}

@keyframes spinner-border {
    to {
        -webkit-transform: rotate(1turn);
        transform: rotate(1turn)
    }
}

.spinner-border {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    vertical-align: text-bottom;
    border: .25em solid;
    border-right: .25em solid transparent;
    border-radius: 50%;
    -webkit-animation: spinner-border .75s linear infinite;
    animation: spinner-border .75s linear infinite;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: .2em
}
</style>
